import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { useInView } from 'react-intersection-observer';
import gql from 'graphql-tag';

import FirstInView from '../FirstInView';
import { useSignUpModal } from '../../contexts/SignUpModal';
import { useFavoriteStatuses } from '../../containers/apollo/withFavoriteStatuses';
import { useFavoriteActions } from '../../containers/apollo/withToggleListFavorite';
import FavoritableItemCard, {
  fragments as cardFragments
} from './FavoritableItemCard';
import StackedCarousel from './StackedCarousel';
import useCarouselImpressions from '../../hooks/useCarouselImpressions';
import {
  RbGtmClient,
  getItemValue,
  parseInventoryItemToAnalyticsItem,
  AnalyticsItemsListIDNameMap,
  AnalyticsItemsList,
  parseWorkToAnalyticsItem
} from '../../lib/gtm';

const FavoritableItemCarousel = ({
  profile,
  expectedCount,
  intl,
  isLoggedIn,
  items,
  loading,
  locale,
  logImpression,
  onContentShowed,
  onFavorite,
  onLoadingShowed,
  requiredItems,
  rows,
  showProductCounts,
  slideWidth,
  transparentCards,
  urlRef,
  showStrikethroughPricing,
  analyticsComponentID,
  analyticsTopic
}) => {
  const [openModal] = useSignUpModal();
  const { isFavorited } = useFavoriteStatuses({ items, isLoggedIn });
  const { addToFavorites, removeFromFavorites } = useFavoriteActions();
  const [ref, inView] = useInView({ triggerOnce: true });
  const hasLogged = useRef(false);

  const reportOnVisibility = useCarouselImpressions({
    items,
    rows,
    logImpression,
    carouselInView: inView,
    loading
  });

  const isWorkItem =
    analyticsComponentID === AnalyticsItemsList.RecommendedWorksCarousel ||
    analyticsComponentID === AnalyticsItemsList.RecommendedWorksPreviewCarousel;

  const analyticsItems = useMemo(() => {
    return isWorkItem
      ? items.map((work, index) =>
          parseWorkToAnalyticsItem({ work, index, analyticsComponentID })
        )
      : items.map((inventoryItem, index) =>
          parseInventoryItemToAnalyticsItem({
            inventoryItem,
            index,
            analyticsComponentID
          })
        );
  }, [items, isWorkItem]);

  useEffect(() => {
    if (
      inView &&
      Array.isArray(items) &&
      items.length > 0 &&
      !hasLogged.current
    ) {
      try {
        RbGtmClient.track({
          event: 'view_item_list',
          page_location: window.location.href,
          topic: analyticsTopic,
          search_query_id: null,
          ecommerce: {
            item_list_id: analyticsComponentID,
            item_list_name: AnalyticsItemsListIDNameMap[analyticsComponentID],
            items: analyticsItems
          }
        });
        hasLogged.current = true;
      } catch (error) {
        console.error('There was error reporting view_item_list event', error);
      }
    }
  }, [inView, items]);

  if (!loading && items.length < requiredItems) return null;

  const itemsToRender = loading
    ? [...new Array(expectedCount + rows)].map(() => ({
        __typename: 'Placeholder'
      }))
    : items;

  return (
    <div>
      {loading ? (
        <FirstInView key="loading-showed" onFirstView={onLoadingShowed} />
      ) : (
        <FirstInView key="content-showed" onFirstView={onContentShowed} />
      )}
      <div ref={ref}>
        <StackedCarousel
          profile={profile}
          rows={rows}
          slideWidth={slideWidth}
          slidesPerView="auto"
          reportOnVisibility={reportOnVisibility}
        >
          {itemsToRender.map((item, index) => {
            const { __typename: type, workId, id } = item;
            const [favorited, favoriteStatus] = isFavorited(item);

            const toggleFavorite = () => {
              onFavorite(item, !favorited, items.indexOf(item));

              if (favorited) {
                removeFromFavorites(favoriteStatus);
              } else {
                addToFavorites(favoriteStatus);
                try {
                  RbGtmClient.track({
                    event: 'add_to_wishlist',
                    ecommerce: {
                      items: [
                        isWorkItem
                          ? parseWorkToAnalyticsItem({
                              work: item,
                              index: items.indexOf(item),
                              analyticsComponentID
                            })
                          : parseInventoryItemToAnalyticsItem({
                              inventoryItem: item,
                              index: items.indexOf(item),
                              analyticsComponentID
                            })
                      ],
                      currency: item.price ? item.price.currency : '',
                      value: getItemValue(item)
                    }
                  });
                } catch (error) {
                  console.error(
                    'There was error reporting add_to_wishlist event',
                    error
                  );
                }
              }
            };

            const showFavorite = !isLoggedIn || !!favoriteStatus;

            const key = `${type}-${workId || id}`;

            return (
              <FavoritableItemCard
                id={key}
                intl={intl}
                item={item}
                favorited={favorited}
                toggleFavorite={toggleFavorite}
                showFavorite={showFavorite}
                isLoggedIn={isLoggedIn}
                openSignUpModal={openModal}
                urlRef={urlRef}
                transparent={transparentCards}
                showProductCount={showProductCounts}
                locale={locale}
                key={key}
                showStrikethroughPricing={showStrikethroughPricing}
                index={index}
                analyticsComponentID={analyticsComponentID}
              />
            );
          })}
        </StackedCarousel>
      </div>
    </div>
  );
};

const workType = PropTypes.shape({
  workId: PropTypes.string
});

const inventoryItemType = PropTypes.shape({
  id: PropTypes.string
});

FavoritableItemCarousel.propTypes = {
  profile: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.oneOfType([workType, inventoryItemType])),
  loading: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  rows: PropTypes.number,
  logImpression: PropTypes.func,
  onLoadingShowed: PropTypes.func,
  onContentShowed: PropTypes.func,
  requiredItems: PropTypes.number,
  onFavorite: PropTypes.func,
  urlRef: PropTypes.string,
  transparentCards: PropTypes.bool,
  showProductCounts: PropTypes.bool,
  slideWidth: PropTypes.number,
  expectedCount: PropTypes.number,
  locale: PropTypes.string,
  intl: intlShape.isRequired,
  showStrikethroughPricing: PropTypes.bool,
  analyticsComponentID: PropTypes.string,
  analyticsTopic: PropTypes.string
};

FavoritableItemCarousel.defaultProps = {
  items: null,
  loading: true,
  isLoggedIn: false,
  rows: 1,
  logImpression: () => {},
  onLoadingShowed: () => {},
  onContentShowed: () => {},
  requiredItems: 1,
  onFavorite: () => {},
  urlRef: null,
  transparentCards: false,
  showProductCounts: true,
  expectedCount: 12,
  locale: 'en',
  slideWidth: 220
};

const WORK_TYPE = 'Work';
const WORK_FRAGMENT_NAME = 'WorkFieldsForFavoritableItemCarousel';
const WORK_FRAGMENT = gql`
  fragment ${WORK_FRAGMENT_NAME} on ${WORK_TYPE} {
    workId
  }
`;

const INVENTORY_ITEM_TYPE = 'ihs_InventoryItemsItem';
const INVENTORY_ITEM_FRAGMENT_NAME =
  'InventoryItemFieldsForFavoritableItemCarousel';
const INVENTORY_ITEM_FRAGMENT = gql`
  fragment ${INVENTORY_ITEM_FRAGMENT_NAME} on ${INVENTORY_ITEM_TYPE} {
    id
  }
`;

export const fragments = [
  {
    type: WORK_TYPE,
    name: WORK_FRAGMENT_NAME,
    fragment: WORK_FRAGMENT
  },
  {
    type: INVENTORY_ITEM_TYPE,
    name: INVENTORY_ITEM_FRAGMENT_NAME,
    fragment: INVENTORY_ITEM_FRAGMENT
  },
  ...cardFragments
];

export default injectIntl(FavoritableItemCarousel);
